/**
 * Una foto, audio, video, documento o contacto que mandó la persona (§9 y §15.3). Se baja por el backend,
 * que lo trae de Twilio con las credenciales de la cuenta (no hay copia propia), y se muestra desde un
 * `blob:` que se libera al desmontar.
 *
 * Qué se baja y cuándo:
 * - imagen, sticker, audio y contacto (chicos): recién cuando la burbuja se ve en pantalla;
 * - video y documento (hasta 20 MB cada uno): NADA hasta que el operador toca «Ver video» o «Descargar».
 *   Antes se bajaban todos los de las burbujas visibles.
 *
 * La descarga crea un `<a download>` sobre el blob y lo clickea: nunca `window.open`, que después de un
 * `await` el navegador bloquea y que además abre otra ventana (R2).
 *
 * El número de un contacto compartido se muestra entero (R3): no queda escondido detrás de un archivo.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, ImageOff, Loader2, RefreshCw, UserRound } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { MedioHilo } from "../../types.conversaciones";
import { leerError } from "../../logic/respuesta";
import { parsearVcard } from "../../logic/conversaciones";

const PEREZOSOS = new Set(["imagen", "sticker", "audio", "contacto"]);

const EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "audio/ogg": "ogg", "audio/mpeg": "mp3", "audio/wav": "wav",
  "audio/amr": "amr", "video/mp4": "mp4", "application/pdf": "pdf", "text/vcard": "vcf", "text/x-vcard": "vcf",
};

const esencia = (contentType: string) => String(contentType || "").split(";")[0].trim().toLowerCase();

function extensionDe(contentType: string): string {
  const ct = esencia(contentType);
  return EXTENSION[ct] || ct.split("/")[1]?.replace(/[^a-z0-9]/g, "").slice(0, 8) || "bin";
}

/**
 * Lo único que se muestra como foto: imágenes rasterizadas. Un SVG es un documento que puede traer scripts: adentro del
 * `<img>` no corren, pero el `blob:` es del origen de la app, y «Abrir imagen en una pestaña nueva» lo abría como página
 * con acceso al token del operador (la CSP que pone el backend no viaja con el blob). Lo demás se ofrece para descargar.
 */
const IMAGENES_EN_LINEA = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

/**
 * El tipo con el que se arma el `blob:`, que sale del archivo que llegó y no del hilo. Fuera de esas fotos y del audio y
 * el video (que el navegador sólo reproduce), todo va como `application/octet-stream`: abierto en otra pestaña se
 * descarga, nunca se interpreta como una página de este origen (HTML, SVG, XML). Un subtipo con `+` queda afuera:
 * `+xml` es XML para el navegador aunque diga audio o video.
 */
function tipoDelBlob(contentType: string): string {
  const t = esencia(contentType);
  return IMAGENES_EN_LINEA.has(t) || /^(audio|video)\/[a-z0-9.-]+$/.test(t) ? t : "application/octet-stream";
}

/** El texto de un blob. `Blob.text()` no está en todos los entornos: si falta, con FileReader. */
function leerTexto(b: Blob): Promise<string> {
  if (typeof b.text === "function") return b.text();
  return new Promise((ok, mal) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result ?? ""));
    r.onerror = () => mal(r.error);
    r.readAsText(b);
  });
}

type Estado =
  | { fase: "espera" }
  | { fase: "cargando" }
  | { fase: "listo"; url: string; contacto: { nombre: string; telefonos: string[] } | null }
  | { fase: "error"; texto: string; reintentar: boolean };

export function MedioEntrante({ medio }: { medio: MedioHilo }) {
  const [estado, setEstado] = useState<Estado>({ fase: "espera" });
  const [pedido, setPedido] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const urlRef = useRef("");
  const vivo = useRef(true);
  // Una «imagen» o un «sticker» que no es una foto rasterizada (un SVG) va como archivo: ni se muestra ni se baja solo.
  const tipo = (medio.tipo === "imagen" || medio.tipo === "sticker") && !IMAGENES_EN_LINEA.has(esencia(medio.contentType))
    ? "otro"
    : medio.tipo;
  const perezoso = PEREZOSOS.has(tipo);
  const nombreArchivo = `archivo-${medio.indice}.${extensionDe(medio.contentType)}`;

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = "";
    };
  }, []);

  /** Baja el medio y devuelve la URL del blob ("" si no se pudo). */
  const cargar = useCallback(async (): Promise<string> => {
    if (!medio.ruta) return "";
    setEstado({ fase: "cargando" });
    try {
      const recibido = await conversacionesApi.medio(medio.ruta);
      const contacto = medio.tipo === "contacto" ? parsearVcard(await leerTexto(recibido).catch(() => "")) : null;
      if (!vivo.current) return "";
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(new Blob([recibido], { type: tipoDelBlob(recibido.type) }));
      urlRef.current = url;
      setEstado({ fase: "listo", url, contacto });
      return url;
    } catch (e) {
      if (!vivo.current) return "";
      const x = leerError(e);
      if (x.status === 404) setEstado({ fase: "error", texto: x.error || "Este archivo ya no está (se borró).", reintentar: false });
      else if (x.status === 413) setEstado({ fase: "error", texto: "Pesa más de 20 MB: no se puede ver desde acá. Pedíselo por otro medio.", reintentar: false });
      else setEstado({ fase: "error", texto: "No pudimos traer el archivo.", reintentar: true });
      return "";
    }
  }, [medio.ruta, medio.tipo]);

  // Carga perezosa de los chicos: recién cuando la burbuja entra en pantalla.
  useEffect(() => {
    if (!perezoso || !medio.ruta) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setPedido(true); return; }
    const io = new IntersectionObserver((entradas) => {
      if (entradas.some((x) => x.isIntersecting)) { setPedido(true); io.disconnect(); }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [perezoso, medio.ruta]);

  useEffect(() => { if (pedido) void cargar(); }, [pedido, cargar]);

  const descargar = async () => {
    const url = urlRef.current || (await cargar());
    if (!url || !vivo.current) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!medio.ruta) {
    return <p className="mt-1 inline-flex items-center gap-1.5 text-xs italic text-slate-500"><ImageOff size={13} /> Archivo borrado</p>;
  }

  const error = estado.fase === "error" ? (
    <div className="mt-1 text-xs text-red-700" role="alert">
      <p>{estado.texto}</p>
      {estado.reintentar && (
        <button type="button" onClick={() => void cargar()} className="mt-1 inline-flex items-center gap-1 font-semibold underline">
          <RefreshCw size={12} /> Reintentar
        </button>
      )}
    </div>
  ) : null;
  const cargando = estado.fase === "cargando" ? <Loader2 size={16} className="animate-spin text-slate-400" aria-label="Cargando el archivo" /> : null;
  const url = estado.fase === "listo" ? estado.url : "";
  const botonDescargar = (texto = "Descargar") => (
    <button type="button" onClick={() => void descargar()} disabled={estado.fase === "cargando"}
      className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 underline disabled:opacity-50">
      <Download size={12} /> {texto}
    </button>
  );

  return (
    <div ref={ref} className="mt-1.5">
      {tipo === "imagen" && (url ? <img src={url} alt="Foto que mandó" className="max-h-72 max-w-full rounded-lg" /> : cargando)}
      {tipo === "sticker" && (url ? <img src={url} alt="Sticker" className="h-28 w-28 object-contain" /> : cargando)}
      {tipo === "audio" && (url ? (
        // NO VERIFICADO: que Safari reproduzca audio/ogg (Opus). Por eso el link de descarga queda siempre.
        <div className="space-y-1">
          <audio controls src={url} className="max-w-full" />
          <a href={url} download={nombreArchivo} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 underline">
            <Download size={12} /> Descargar audio
          </a>
        </div>
      ) : cargando)}
      {tipo === "video" && (url ? <video controls src={url} className="max-h-72 max-w-full rounded-lg" /> : estado.fase === "espera" ? (
        <button type="button" onClick={() => setPedido(true)} className="text-xs font-semibold text-violet-700 underline">Ver video</button>
      ) : cargando)}
      {(tipo === "documento" || tipo === "otro") && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          <FileText size={15} className="text-slate-400" />
          <span>{tipo === "documento" ? "Documento" : "Archivo"} ({medio.contentType || "sin tipo"})</span>
          {botonDescargar()}
          {cargando}
        </div>
      )}
      {tipo === "contacto" && (
        <div className="text-sm text-slate-700">
          <div className="flex items-start gap-1.5">
            <UserRound size={15} className="mt-0.5 shrink-0 text-slate-400" />
            {estado.fase === "listo" && estado.contacto ? (
              <div>
                {(estado.contacto.telefonos.length ? estado.contacto.telefonos : [""]).map((t, i) => (
                  <p key={`${t}-${i}`}>
                    Contacto: {estado.contacto?.nombre || "sin nombre"}{t && <> · <span className="whitespace-nowrap">{t}</span></>}
                  </p>
                ))}
              </div>
            ) : estado.fase === "listo" || estado.fase === "error" ? <p>Contacto compartido</p> : cargando}
          </div>
          <div className="mt-1">{botonDescargar()}</div>
        </div>
      )}
      {error}
    </div>
  );
}

export default MedioEntrante;
